import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Spin, Tag, Image, Select, Upload, Card, Row, Col, Radio, Divider, Switch, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LoadingOutlined, UploadOutlined, SortAscendingOutlined, LinkOutlined, GlobalOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getBannerImages, uploadBannerImage, deleteBannerFolder, BlobItem } from '../../services/blobService';

// 定义轮播图类型
interface CarouselItem {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  mobileBannerUrl?: string; // 移动端图片URL
  linkUrl?: string;
  order: number;
  status: 'active' | 'inactive';
  createdAt?: string;
  updatedAt?: string;
  translations?: {
    title?: string;
    description?: string;
  };
}

// 转换Blob对象为轮播图对象
const convertBlobToCarousel = (blob: any, index: number): CarouselItem => {
  const fileName = blob.pathname ? blob.pathname.split('/').pop() || '' : '';
  const title = fileName.replace(/\.[^/.]+$/, '');
  
  return {
    id: blob.pathname || blob.id || `carousel-${index}`,
    title: blob.title || title,
    imageUrl: blob.url || blob.imageUrl,
    mobileBannerUrl: blob.mobileBannerUrl || '', // 初始化移动端图片URL
    linkUrl: blob.link || blob.linkUrl || '', // 默认空链接
    order: blob.order || index + 1, // 默认顺序
    status: 'active', // 默认为启用状态
    createdAt: blob.uploadedAt || blob.createdAt,
    updatedAt: blob.uploadedAt || blob.updatedAt
  };
};

// 样式组件
const ActionBar = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 12px;
    
    .ant-space {
      display: flex;
      justify-content: flex-start;
      gap: 8px !important;
      
      .ant-btn {
        flex: none;
        width: auto;
      }
    }
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  background: #fff;
  border-radius: 8px;
  margin: 16px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 576px) {
    padding: 24px;
    margin: 12px 0;
  }
`;

const StyledModal = styled(Modal)`
  @media (max-width: 576px) {
    max-width: calc(100vw - 32px);
    
    .ant-modal-content {
      padding: 12px;
    }
    
    .ant-form-item {
      margin-bottom: 12px;
    }
  }
`;

const StyledBannerImage = styled(Image)`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  
  @media (max-width: 576px) {
    height: 80px;
  }
`;

const BannerCard = styled(Card)`
  margin-bottom: 16px;
  
  .banner-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  
  .banner-title {
    font-size: 16px;
    font-weight: bold;
  }
  
  .banner-description {
    color: #666;
    margin-bottom: 8px;
  }
  
  .banner-link {
    color: #1890ff;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .banner-order {
    font-weight: bold;
    margin-right: 8px;
  }
`;

const StyledTableImage = styled(Image)`
  width: 160px;
  height: 60px;
  object-fit: cover;
  background: #f5f5f5;
  border-radius: 4px;

  @media (max-width: 576px) {
    width: 120px;
    height: 45px;
  }
`;

const ImageUploadContainer = styled.div`
  .image-preview {
    margin-top: 16px;
    position: relative;
    background: #f5f5f5;
    border-radius: 4px;
    overflow: hidden;
    
    .ant-image {
      width: 100%;
      
      img {
        width: 100%;
        max-height: 300px;
        object-fit: contain;
      }
    }
    
    .image-actions {
      position: absolute;
      top: 8px;
      right: 8px;
      
      button {
        margin-left: 8px;
        background: rgba(255, 255, 255, 0.8);
        border: none;
        
        &:hover {
          background: #fff;
        }
      }
    }
  }
`;

const StyledPreviewImage = styled(Image)`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  
  @media (max-width: 576px) {
    height: 80px;
  }
`;

// 轮播图管理组件
const Carousels: React.FC = () => {
  const [carousels, setCarousels] = useState<CarouselItem[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCarousel, setEditingCarousel] = useState<CarouselItem | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<RcFile | null>(null);
  const [mobileImagePreview, setMobileImagePreview] = useState<string>(''); // 添加移动端图片预览
  const [mobileImageFile, setMobileImageFile] = useState<RcFile | null>(null); // 添加移动端图片文件
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [tableLoading, setTableLoading] = useState(false);
  const { t, i18n } = useTranslation();

  // 获取轮播图列表
  const fetchCarousels = async () => {
    try {
      setLoading(true);
      const blobImages = await getBannerImages();
      console.log('获取到的轮播图数据:', blobImages);
      setCarousels(blobImages);
    } catch (error) {
      console.error('获取轮播图失败:', error);
      message.error('获取轮播图失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchCarousels();
  }, []);

  // 添加轮播图
  const handleAdd = () => {
    setEditingCarousel(null);
    setImagePreview('');
    setMobileImagePreview('');
    setImageFile(null);
    setMobileImageFile(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      order: carousels.length + 1
    });
    setIsModalVisible(true);
  };

  // 编辑轮播图
  const handleEdit = (carousel: CarouselItem) => {
    setEditingCarousel(carousel);
    setImagePreview(carousel.imageUrl);
    setMobileImagePreview(carousel.mobileBannerUrl || ''); // 设置移动端图片预览
    setImageFile(null);
    setMobileImageFile(null); // 重置移动端图片文件
    form.setFieldsValue({
      title: carousel.title,
      description: carousel.description || '',
      imageUrl: carousel.imageUrl,
      mobileBannerUrl: carousel.mobileBannerUrl || '', // 设置表单中的移动端图片URL
      linkUrl: carousel.linkUrl || '',
      order: carousel.order,
      status: carousel.status,
      translations: carousel.translations || {
        title: '',
        description: ''
      }
    });
    setIsModalVisible(true);
  };

  // 删除轮播图
  const handleDelete = async (carousel: CarouselItem) => {
    if (!carousel.id) {
      message.error('无效的轮播图ID');
      return;
    }
    
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除"${carousel.title || '未命名轮播图'}"吗？这将同时删除Web端和移动端图片。`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: '删除中...', key: 'deleteCarousel', duration: 0 });
          console.log('开始删除轮播图:', carousel.id);
          
          // 使用新的deleteBannerFolder函数删除整个文件夹
          const deleteSuccess = await deleteBannerFolder(carousel.id);
          
          if (deleteSuccess) {
            console.log('轮播图文件夹删除成功');
            await fetchCarousels();
            message.success({ content: '删除成功!', key: 'deleteCarousel' });
          } else {
            console.warn('轮播图文件夹删除可能未完成，尝试刷新数据');
            message.warning({ content: '文件删除可能未完成，正在尝试同步数据...', key: 'deleteCarousel', duration: 2 });
            
            try {
              await fetchCarousels();
              message.info({ content: '数据已更新，请检查删除是否成功', key: 'deleteCarousel', duration: 3 });
            } catch (dbError) {
              console.error('刷新数据失败:', dbError);
              message.error({ content: '刷新数据失败', key: 'deleteCarousel' });
            }
          }
        } catch (error) {
          console.error('删除轮播图失败:', error);
          message.error({ 
            content: `删除失败: ${error instanceof Error ? error.message : '未知错误'}`, 
            key: 'deleteCarousel' 
          });
        }
      }
    });
  };

  // 图片上传前处理 - Web端
  const handleImageSelect = async (file: RcFile) => {
    try {
      setUploadLoading(true);
      console.log('选择Web端图片:', file.name, '大小:', file.size, '类型:', file.type);
      
      // 检查文件类型
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('请选择图片文件');
        setUploadLoading(false);
        return false;
      }
      
      // 检查文件大小 (5MB限制)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('图片必须小于5MB');
        setUploadLoading(false);
        return false;
      }
      
      // 创建预览
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setImageFile(file);
      form.setFieldsValue({ imageUrl: previewUrl });
      
      console.log('Web端图片预览URL已创建');
      setUploadLoading(false);
      return false; // 阻止默认上传行为
    } catch (error) {
      console.error('图片处理失败:', error);
      message.error('图片处理失败');
      setUploadLoading(false);
      return false;
    }
  };

  // 图片上传前处理 - 移动端
  const handleMobileImageSelect = async (file: RcFile) => {
    try {
      setUploadLoading(true);
      console.log('选择移动端图片:', file.name, '大小:', file.size, '类型:', file.type);
      
      // 检查文件类型
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('请选择图片文件');
        setUploadLoading(false);
        return false;
      }
      
      // 检查文件大小 (5MB限制)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('图片必须小于5MB');
        setUploadLoading(false);
        return false;
      }
      
      // 创建预览
      const previewUrl = URL.createObjectURL(file);
      setMobileImagePreview(previewUrl);
      setMobileImageFile(file);
      form.setFieldsValue({ mobileBannerUrl: previewUrl });
      
      console.log('移动端图片预览URL已创建');
      setUploadLoading(false);
      return false; // 阻止默认上传行为
    } catch (error) {
      console.error('移动端图片处理失败:', error);
      message.error('移动端图片处理失败');
      setUploadLoading(false);
      return false;
    }
  };

  // 模态框确认
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (!editingCarousel && !imageFile) {
        message.error('请上传Web端轮播图片');
        return;
      }

      setLoading(true);
      setUploadLoading(true);
      
      // 上传两个尺寸的图片
      let folderId = editingCarousel?.id;
      let webUploadResult: BlobItem | undefined;
      let mobileUploadResult: BlobItem | undefined;
      
      // 上传Web端图片
      if (imageFile) {
        try {
          console.log('准备上传Web端轮播图', {
            title: values.title,
            fileSize: imageFile.size,
            fileType: imageFile.type
          });
          
          // 使用blobService中的上传函数
          webUploadResult = await uploadBannerImage(imageFile, {
            title: values.title,
            isWeb: true,
            folderId: folderId,
            order: values.order
          });
          
          console.log('Web端图片上传成功:', webUploadResult);
          
          // 设置Web端图片URL和文件夹ID
          values.imageUrl = webUploadResult.url;
          
          // 如果是新建，获取生成的文件夹ID
          if (!folderId) {
            folderId = webUploadResult.pathname.split('/')[1];
          }
        } catch (error) {
          console.error('Web端图片上传失败:', error);
          message.error(`Web端图片上传失败: ${error instanceof Error ? error.message : '未知错误'}`);
          setLoading(false);
          setUploadLoading(false);
          return;
        }
      }
      
      // 上传移动端图片
      if (mobileImageFile) {
        try {
          console.log('准备上传移动端轮播图', {
            title: values.title,
            fileSize: mobileImageFile.size,
            fileType: mobileImageFile.type
          });
          
          // 使用blobService中的上传函数，使用相同的文件夹ID
          mobileUploadResult = await uploadBannerImage(mobileImageFile, {
            title: values.title,
            isWeb: false,
            folderId: folderId, // 使用相同的文件夹ID
            order: values.order
          });
          
          console.log('移动端图片上传成功:', mobileUploadResult);
          
          // 设置移动端图片URL
          values.mobileBannerUrl = mobileUploadResult.url;
        } catch (error) {
          console.error('移动端图片上传失败:', error);
          message.warning(`移动端图片上传失败: ${error instanceof Error ? error.message : '未知错误'}, 但Web端图片已上传成功`);
        }
      }
      
      message.success(editingCarousel ? '更新成功' : '添加成功');
      
      // 重新获取轮播图列表
      await fetchCarousels();
      setIsModalVisible(false);
      setImageFile(null);
      setMobileImageFile(null);
      setImagePreview('');
      setMobileImagePreview('');
      form.resetFields();
    } catch (error) {
      console.error('表单提交失败:', error);
      message.error('操作失败');
    } finally {
      setLoading(false);
      setUploadLoading(false);
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
      width: 80,
      sorter: (a: CarouselItem, b: CarouselItem) => a.order - b.order,
    },
    {
      title: '图片',
      key: 'image',
      width: 180,
      render: (record: CarouselItem) => (
        <StyledTableImage
          src={record.imageUrl}
          alt={record.title}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAFUlEQVR42mNkYGAYBaMhMBoCoyEAAAgAAAEXKVg8AAAAAElFTkSuQmCC"
        />
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 150,
    },
    {
      title: '上传时间',
      key: 'uploadedAt',
      width: 180,
      render: (record: CarouselItem) => (
        record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: CarouselItem) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 轮播图网格视图
  const CarouselGrid = () => (
    <div>
      {carousels.sort((a, b) => a.order - b.order).map(carousel => (
        <BannerCard key={carousel.id}>
          <div className="banner-header">
            <Space>
              <span className="banner-order">#{carousel.order}</span>
              <span className="banner-title">{carousel.title}</span>
              <Tag color={carousel.status === 'active' ? 'success' : 'default'}>
                {carousel.status === 'active' ? '启用' : '禁用'}
              </Tag>
            </Space>
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(carousel)}
              >
                编辑
              </Button>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(carousel)}
              >
                删除
              </Button>
            </Space>
          </div>
          {carousel.description && <div className="banner-description">{carousel.description}</div>}
          {carousel.linkUrl && (
            <div className="banner-link">
              <LinkOutlined /> <a href={carousel.linkUrl} target="_blank" rel="noopener noreferrer">{carousel.linkUrl}</a>
            </div>
          )}
          
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>
                <Tag color="blue">Web端图片</Tag>
              </div>
              <StyledBannerImage
                src={carousel.imageUrl}
                alt={`${carousel.title} - Web版本`}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAFUlEQVR42mNkYGAYBaMhMBoCoyEAAAgAAAEXKVg8AAAAAElFTkSuQmCC"
              />
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>
                <Tag color="green">移动端图片</Tag>
                {!carousel.mobileBannerUrl && <Tag color="orange">未设置</Tag>}
              </div>
              {carousel.mobileBannerUrl ? (
                <StyledBannerImage
                  src={carousel.mobileBannerUrl}
                  alt={`${carousel.title} - 移动版本`}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAFUlEQVR42mNkYGAYBaMhMBoCoyEAAAgAAAEXKVg8AAAAAElFTkSuQmCC"
                />
              ) : (
                <div style={{ 
                  height: 120, 
                  background: '#f5f5f5', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: 4,
                  color: '#999'
                }}>
                  未设置移动端图片
                </div>
              )}
            </Col>
          </Row>
        </BannerCard>
      ))}
    </div>
  );

  if (loading && carousels.length === 0) {
    return (
      <LoadingContainer>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </LoadingContainer>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <ActionBar>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加轮播图
          </Button>
        </Space>
        <Radio.Group 
          value={viewMode} 
          onChange={e => setViewMode(e.target.value)} 
          buttonStyle="solid"
          size={window.innerWidth <= 576 ? 'small' : 'middle'}
        >
          <Radio.Button value="grid">网格视图</Radio.Button>
          <Radio.Button value="table">表格视图</Radio.Button>
        </Radio.Group>
      </ActionBar>

      {loading ? (
        <LoadingContainer>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </LoadingContainer>
      ) : carousels.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          background: '#fff', 
          borderRadius: '8px',
          marginTop: '16px'
        }}>
          <p>暂无轮播图，请点击"添加轮播图"按钮添加</p>
        </div>
      ) : viewMode === 'grid' ? (
        <CarouselGrid />
      ) : (
        <div style={{ 
          background: '#fff', 
          padding: '16px', 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          overflowX: 'auto'
        }}>
          <Table 
            columns={columns} 
            dataSource={carousels} 
            rowKey="id"
            pagination={false}
            scroll={{ x: 1000 }}
            size="small"
          />
        </div>
      )}

      {/* 编辑/添加轮播图模态框 */}
      <StyledModal
        title={editingCarousel ? '编辑轮播图' : '添加轮播图'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setImagePreview('');
          setMobileImagePreview('');
          setImageFile(null);
          setMobileImageFile(null);
          form.resetFields();
        }}
        width={800}
        centered
      >
        <Form form={form} layout="vertical">
          <Divider orientation="left">基本信息</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="轮播图标题"
                rules={[{ required: true, message: '请输入轮播图标题' }]}
                help="标题将用于文件名，建议简单明了"
              >
                <Input placeholder="请输入标题" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['translations', 'title']}
                label={
                  <Space>
                    <span>标题（英文）</span>
                    <GlobalOutlined />
                  </Space>
                }
              >
                <Input placeholder="请输入英文标题" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="description"
                label="描述"
              >
                <Input.TextArea rows={3} placeholder="请输入描述文字" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['translations', 'description']}
                label={
                  <Space>
                    <span>描述（英文）</span>
                    <GlobalOutlined />
                  </Space>
                }
              >
                <Input.TextArea rows={3} placeholder="请输入英文描述" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="linkUrl"
                label="链接地址"
                help="设置点击轮播图后跳转的链接，如不需要可留空"
              >
                <Input placeholder="例如: /products?category=new" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="order"
                label="显示顺序"
                rules={[{ required: true, message: '请输入显示顺序' }]}
                help="数字越小，显示越靠前"
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Select.Option value="active">启用</Select.Option>
                  <Select.Option value="inactive">禁用</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">轮播图片上传</Divider>
          
          {/* Web端图片上传 */}
          <Form.Item
            name="imageUrl"
            label={<Space>Web端图片 <Tag color="blue">推荐尺寸: 1900x600px</Tag></Space>}
            rules={[{ required: true, message: '请上传Web端轮播图片' }]}
            help="用于桌面设备展示的轮播图"
          >
            <ImageUploadContainer>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageSelect}
                maxCount={1}
                disabled={uploadLoading}
              >
                <Button icon={<UploadOutlined />} loading={uploadLoading} disabled={uploadLoading}>
                  {imagePreview ? '更换Web端图片' : '选择Web端图片'}
                </Button>
              </Upload>

              {imagePreview && (
                <div className="image-preview">
                  <StyledPreviewImage
                    src={imagePreview || form.getFieldValue('imageUrl')}
                    alt="Web端轮播图预览"
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAFUlEQVR42mNkYGAYBaMhMBoCoyEAAAgAAAEXKVg8AAAAAElFTkSuQmCC"
                  />
                  <div className="image-actions">
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setImageFile(null);
                        form.setFieldsValue({ imageUrl: '' });
                      }}
                    />
                  </div>
                </div>
              )}
            </ImageUploadContainer>
          </Form.Item>

          {/* 移动端图片上传 */}
          <Form.Item
            name="mobileBannerUrl"
            label={<Space>移动端图片 <Tag color="green">推荐尺寸: 750x500px</Tag></Space>}
            help="用于手机设备展示的轮播图，如不上传将在移动端使用Web端图片"
          >
            <ImageUploadContainer>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleMobileImageSelect}
                maxCount={1}
                disabled={uploadLoading}
              >
                <Button icon={<UploadOutlined />} loading={uploadLoading} disabled={uploadLoading}>
                  {mobileImagePreview ? '更换移动端图片' : '选择移动端图片'}
                </Button>
              </Upload>

              {mobileImagePreview && (
                <div className="image-preview">
                  <StyledPreviewImage
                    src={mobileImagePreview || form.getFieldValue('mobileBannerUrl')}
                    alt="移动端轮播图预览"
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAFUlEQVR42mNkYGAYBaMhMBoCoyEAAAgAAAEXKVg8AAAAAElFTkSuQmCC"
                  />
                  <div className="image-actions">
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileImagePreview('');
                        setMobileImageFile(null);
                        form.setFieldsValue({ mobileBannerUrl: '' });
                      }}
                    />
                  </div>
                </div>
              )}
            </ImageUploadContainer>
          </Form.Item>
          
        </Form>
      </StyledModal>
    </div>
  );
};

export default Carousels; 